import { combineReducers } from 'redux';
import { Action, handleActions, ReducerMap } from 'redux-actions';
import * as actions from '../actions';
import initialState from '../store/initialState';
import { toActionType } from '../utils/helpers/redux';
import {
  Card,
  CompanyNotification,
  ItemConnectionSort
} from '../graphql/types';
import { FullItemData } from '../components/Ad/Sections/CurrentItemSections/types';
import {
  AddNewItemModalState,
  AdriverBannerState,
  AuthModalReducerPayload,
  AuthModalState,
  CurrentItemReducerPayload,
  CurrentItemState,
  FailurePaymentModalViewState,
  FailureWebviewPaymentStatusModalViewState,
  ModalLockState,
  NotificationState,
  ProfileState,
  RefetchModalDataState,
  ResultByNumber,
  SearchFormReducerPayload,
  SearchFormState,
  SimpleModal,
  SuccessPromotionModalViewState,
  SuccessPromotionReducerPayload,
  SuccessWebviewPaymentStatusModalViewState,
  UserCardsState
} from '../store/types';
import { SearchFormValues } from '../utils/helpers/search';

const reducerMap = {
  [toActionType(actions.switchTab)](
    state: SearchFormState,
    { payload }: Action<number>
  ) {
    return { ...state, currentTabId: payload };
  },
  [toActionType(actions.setSearchSort)](
    state: SearchFormState,
    { payload }: Action<ItemConnectionSort>
  ) {
    return { ...state, currentSortType: payload };
  },
  [toActionType(actions.showPopupHeader)](state: SearchFormState) {
    return { ...state, 'popup-header': true };
  },
  [toActionType(actions.hidePopupHeader)](state: SearchFormState) {
    return { ...state, 'popup-header': false };
  },
  [toActionType(actions.setCurrentCity)](
    state: SearchFormState,
    { payload }: Action<string>
  ) {
    return { ...state, currentCity: payload };
  },
  [toActionType(actions.setSearchValues)](
    state: SearchFormState,
    { payload }: Action<SearchFormValues>
  ) {
    return { ...state, values: payload };
  },
  [toActionType(actions.setResetPopupHandler)](
    state: SearchFormState,
    { payload }: Action<(() => void) | null>
  ) {
    return { ...state, resetPopupHandler: payload };
  },
  [toActionType(actions.setLocationsResetButtonIsDisabled)](
    state: SearchFormState,
    { payload }: Action<boolean>
  ) {
    return { ...state, locationsResetButtonIsDisabled: payload };
  },
  [toActionType(actions.setResetButtonIsDisabled)](
    state: SearchFormState,
    { payload }: Action<boolean>
  ) {
    return { ...state, resetButtonIsDisabled: payload };
  },
  [toActionType(actions.setChosenLocationNames)](
    state: SearchFormState,
    { payload }: Action<string[]>
  ) {
    return { ...state, chosenLocationNames: payload };
  },
  [toActionType(actions.setLocationIds)](
    state: SearchFormState,
    { payload }: Action<string[]>
  ) {
    return { ...state, locationIds: payload };
  },
  [toActionType(actions.setCurrentState)](
    state: SearchFormState,
    { payload }: Action<SearchFormValues | null>
  ) {
    return { ...state, currentState: payload };
  },
  [toActionType(actions.setIsLoadingResultByNumber)](
    state: SearchFormState,
    { payload }: Action<boolean>
  ) {
    return { ...state, isLoadingResultByNumber: payload };
  },
  [toActionType(actions.setResultByNumber)](
    state: SearchFormState,
    { payload }: Action<ResultByNumber>
  ) {
    return { ...state, resultByNumber: payload };
  },
  [toActionType(actions.setCategoryIds)](
    state: SearchFormState,
    { payload }: Action<(string | undefined)[]>
  ) {
    return { ...state, categoryIds: payload };
  }
};

export const searchForm = handleActions<
  SearchFormState,
  SearchFormReducerPayload
>(
  reducerMap as ReducerMap<SearchFormState, SearchFormReducerPayload>,
  initialState.searchForm
);

export const authModal = handleActions<AuthModalState, AuthModalReducerPayload>(
  {
    [toActionType(actions.toggleAuthModal)](
      state: AuthModalState,
      { payload }: Action<boolean | undefined>
    ) {
      const updatedState =
        typeof payload === 'boolean' ? payload : !state.isVisible;
      return { ...state, isVisible: updatedState };
    },
    [toActionType(actions.setEmail)](
      state: AuthModalState,
      { payload }: Action<string>
    ) {
      return { ...state, email: payload };
    }
  } as ReducerMap<AuthModalState, AuthModalReducerPayload>,
  initialState.authModal
);

export const notification = handleActions<
  NotificationState,
  Partial<NotificationState>
>(
  {
    [toActionType(actions.setNotification)](
      state,
      { payload }: Action<Partial<NotificationState>>
    ) {
      return { ...state, ...payload };
    },
    [toActionType(actions.closeNotification)](state) {
      return { ...state, isClosing: true };
    },
    [toActionType(actions.clearNotificationMessage)]() {
      return initialState.notification;
    }
  },
  initialState.notification
);

export const drawerUI = handleActions<SimpleModal, boolean | undefined>(
  {
    [toActionType(actions.toggleDrawer)](
      state,
      { payload }: Action<boolean | undefined>
    ) {
      const updatedState =
        typeof payload === 'boolean' ? payload : !state.isVisible;
      return { ...state, isVisible: updatedState };
    }
  },
  initialState.drawerUI
);

const errorUI = handleActions<{ isError: boolean }, boolean>(
  {
    [toActionType(actions.setError)](state, { payload }: Action<boolean>) {
      return { ...state, isError: payload };
    }
  },
  initialState.errorUI
);

const blockerDetector = handleActions<{ isBlockerActive: boolean }, boolean>(
  {
    [toActionType(actions.setBlockerDetector)](
      state,
      { payload }: Action<boolean>
    ) {
      return { ...state, isBlockerActive: payload };
    }
  },
  initialState.blockerDetector
);

export const sideMenuModal = handleActions<SimpleModal, boolean | undefined>(
  {
    [toActionType(actions.toggleSideMenuModal)](
      state,
      { payload }: Action<boolean | undefined>
    ) {
      const updatedState =
        typeof payload === 'boolean' ? payload : !state.isVisible;
      return { ...state, isVisible: updatedState };
    }
  },
  initialState.sideMenuModal
);

export const addNewItemModal = handleActions<
  AddNewItemModalState,
  boolean | undefined
>(
  {
    [toActionType(actions.toggleAddNewItemModal)](
      state,
      { payload }: Action<boolean | undefined>
    ) {
      const updatedState =
        typeof payload === 'boolean' ? payload : !state.isVisible;
      return { ...state, isVisible: updatedState };
    },
    [toActionType(actions.toggleAuthWindow)](
      state,
      { payload }: Action<boolean | undefined>
    ) {
      const updatedState =
        typeof payload === 'boolean' ? payload : !state.isOpenAuthWindow;
      return { ...state, isOpenAuthWindow: updatedState };
    },
    [toActionType(actions.setCreationResetButtonIsDisabled)](
      state,
      { payload }: Action<boolean>
    ) {
      return { ...state, creationResetButtonIsDisabled: payload };
    },
    [toActionType(actions.setIsBannedUser)](
      state,
      { payload }: Action<boolean>
    ) {
      return { ...state, isBannedUser: payload };
    }
  },
  initialState.addNewItemModal
);

export const promotionModalView = handleActions<
  SimpleModal,
  boolean | undefined
>(
  {
    [toActionType(actions.togglePromotionModalView)](
      state,
      { payload }: Action<boolean | undefined>
    ) {
      const updatedState =
        typeof payload === 'boolean' ? payload : !state.isVisible;
      return { ...state, isVisible: updatedState };
    }
  },
  initialState.promotionModalView
);

export const restoreAlertModal = handleActions<
  SimpleModal,
  boolean | undefined
>(
  {
    [toActionType(actions.toggleRestoreAlertModal)](
      state,
      { payload }: Action<boolean | undefined>
    ) {
      const updatedState =
        typeof payload === 'boolean' ? payload : !state.isVisible;
      return { ...state, isVisible: updatedState };
    }
  },
  initialState.restoreAlertModal
);

export const successPromotionModalView = handleActions<
  SuccessPromotionModalViewState,
  SuccessPromotionReducerPayload
>(
  {
    [toActionType(actions.openSuccessPromotionAlertModal)](
      state: SuccessPromotionModalViewState,
      { payload }: Action<string>
    ) {
      return { ...state, isVisible: true, name: payload };
    },
    [toActionType(actions.closeSuccessPromotionAlertModal)](
      state: SuccessPromotionModalViewState
    ) {
      return { ...state, isVisible: false, name: null };
    },
    [toActionType(actions.setSuccessPromotionItemData)](
      state: SuccessPromotionModalViewState,
      { payload }: Action<SuccessPromotionModalViewState['itemData']>
    ) {
      return { ...state, itemData: payload };
    }
  } as ReducerMap<
    SuccessPromotionModalViewState,
    SuccessPromotionReducerPayload
  >,
  initialState.successPromotionModalView
);

export const refetchModalData = handleActions<RefetchModalDataState, boolean>(
  {
    [toActionType(actions.toggleRefetchModalData)](
      state,
      { payload }: Action<boolean>
    ) {
      return { shouldRefetch: payload };
    }
  },
  initialState.refetchModalData
);

export const failurePaymentModalView = handleActions<
  FailurePaymentModalViewState,
  FailurePaymentModalViewState
>(
  {
    [toActionType(actions.openFailurePaymentAlertModal)](
      state,
      { payload }: Action<FailurePaymentModalViewState>
    ) {
      return {
        isVisible: payload.isVisible,
        name: payload.name,
        shouldReload: payload.shouldReload
      };
    }
  },
  initialState.failurePaymentModalView
);

export const itemsBalanceModalView = handleActions<SimpleModal, boolean>(
  {
    [toActionType(actions.openItemsBalanceModal)](
      state,
      { payload }: Action<boolean>
    ) {
      return { isVisible: payload };
    }
  },
  initialState.itemsBalanceModalView
);

export const buyItemsModalView = handleActions<SimpleModal, boolean>(
  {
    [toActionType(actions.openBuyItemsModal)](
      state,
      { payload }: Action<boolean>
    ) {
      return { isVisible: payload };
    }
  },
  initialState.buyItemsModalView
);

export const walletPayModalView = handleActions<SimpleModal, boolean>(
  {
    [toActionType(actions.openWalletPayModal)](
      state,
      { payload }: Action<boolean>
    ) {
      return { isVisible: payload };
    }
  },
  initialState.walletPayModalView
);

export const terminalModalView = handleActions<SimpleModal, boolean>(
  {
    [toActionType(actions.openTerminalModal)](
      state,
      { payload }: Action<boolean>
    ) {
      return { isVisible: payload };
    }
  },
  initialState.terminalModalView
);

export const personalAccountModalView = handleActions<SimpleModal, boolean>(
  {
    [toActionType(actions.openPersonalAccountModal)](
      state,
      { payload }: Action<boolean>
    ) {
      return { isVisible: payload };
    }
  },
  initialState.personalAccountModalView
);

export const profilePromotionItemId = handleActions<
  { profileItemId: string | null | undefined },
  string | null | undefined
>(
  {
    [toActionType(actions.setProfilePromotionItemId)](
      state,
      { payload }: Action<string | null | undefined>
    ) {
      return { profileItemId: payload };
    }
  },
  initialState.profilePromotionItemId
);

export const profileEditSuccessMessage = handleActions<SimpleModal, boolean>(
  {
    [toActionType(actions.showProfileEditSuccessMessage)](
      state,
      { payload }: Action<boolean>
    ) {
      return { isVisible: payload };
    }
  },
  initialState.profileEditSuccessMessage
);

export const profilePromotionItemsModalView = handleActions<
  SimpleModal,
  boolean
>(
  {
    [toActionType(actions.toggleProfilePromotionItemsModal)](
      state,
      { payload }: Action<boolean>
    ) {
      return { isVisible: payload };
    }
  },
  initialState.profilePromotionItemsModalView
);

export const packagePayModalView = handleActions<SimpleModal, boolean>(
  {
    [toActionType(actions.openPackagePayModal)](
      state,
      { payload }: Action<boolean>
    ) {
      return { isVisible: payload };
    }
  },
  initialState.packagePayModalView
);

export const successWebViewPaymentStatusModalView = handleActions<
  SuccessWebviewPaymentStatusModalViewState,
  SuccessWebviewPaymentStatusModalViewState['payment']
>(
  {
    [toActionType(actions.openSuccessWebViewPaymentStatusAlertModal)](
      state,
      { payload }: Action<SuccessWebviewPaymentStatusModalViewState['payment']>
    ) {
      return { isVisible: true, payment: payload };
    },
    [toActionType(actions.closeSuccessWebViewPaymentStatusAlertModal)](state) {
      return { isVisible: false, payment: null };
    }
  },
  initialState.successWebviewPaymentStatusModalView
);

export const failureWebViewPaymentStatusModalView = handleActions<
  FailureWebviewPaymentStatusModalViewState,
  {
    isVisible: boolean;
    type: string;
    shouldReload?: boolean;
    errorMessage?: string | null;
  }
>(
  {
    [toActionType(actions.openFailureWebViewPaymentStatusAlertModal)](
      state,
      {
        payload
      }: Action<{
        isVisible: boolean;
        type: string;
        shouldReload?: boolean;
        errorMessage?: string | null;
      }>
    ) {
      return {
        isVisible: payload.isVisible,
        type: payload.type ?? state.type,
        errorMessage: payload.isVisible ? (payload.errorMessage ?? null) : null
      };
    }
  },
  initialState.failureWebviewPaymentStatusModalView
);

export const replenishmentNotifications = handleActions<
  CompanyNotification[],
  CompanyNotification[] | undefined
>(
  {
    [toActionType(actions.setReplenishmentNotifications)](
      state,
      { payload }: Action<CompanyNotification[] | undefined>
    ) {
      return payload ?? state;
    }
  },
  initialState.replenishmentNotifications
);

export const replenishmentNotificationsModal = handleActions<
  SimpleModal,
  boolean | undefined
>(
  {
    [toActionType(actions.toggleReplenishmentNotificationsModal)](
      state,
      { payload }: Action<boolean | undefined>
    ) {
      const updatedState =
        typeof payload === 'boolean' ? payload : !state.isVisible;
      return { ...state, isVisible: updatedState };
    }
  },
  initialState.replenishmentNotificationsModal
);

export const userCards = handleActions<UserCardsState, Card[]>(
  {
    [toActionType(actions.setUserCardsState)](
      state,
      { payload }: Action<Card[]>
    ) {
      return { cardsList: payload };
    }
  },
  initialState.userCards
);

export const adriverBanner = handleActions<AdriverBannerState, string>(
  {
    [toActionType(actions.setAdriverSelectedCity)](
      state,
      { payload }: Action<string>
    ) {
      return { selectedCityName: payload };
    }
  },
  initialState.adriverBanner
);

export const currentItem = handleActions<
  CurrentItemState,
  CurrentItemReducerPayload
>(
  {
    [toActionType(actions.setCurrentItemAuthModalOpen)](
      state: CurrentItemState,
      { payload }: Action<boolean>
    ) {
      return { ...state, isAuthModalOpen: payload };
    },
    [toActionType(actions.setCurrentItem)](
      state: CurrentItemState,
      { payload }: Action<NonNullable<FullItemData>>
    ) {
      return { ...state, currentItem: payload };
    }
  } as ReducerMap<CurrentItemState, CurrentItemReducerPayload>,
  initialState.currentItem
);

export const profile = handleActions<ProfileState, boolean>(
  {
    [toActionType(actions.setProfileTransfersModalVisible)](
      state,
      { payload }: Action<boolean>
    ) {
      return { ...state, isTransferModalVisible: payload };
    },
    [toActionType(actions.setProfileMenuModalVisible)](
      state,
      { payload }: Action<boolean>
    ) {
      return { ...state, profileMenuModalIsVisible: payload };
    }
  },
  initialState.profile
);

export const modalLock = handleActions<ModalLockState, number>(
  {
    [toActionType(actions.setModalLock)](state, { payload }: Action<number>) {
      return { ...state, activeLocks: payload };
    }
  },
  initialState.modalLock
);

export default combineReducers({
  authModal,
  addNewItemModal,
  sideMenuModal,
  promotionModalView,
  restoreAlertModal,
  drawerUI,
  searchForm,
  errorUI,
  blockerDetector,
  successPromotionModalView,
  refetchModalData,
  failurePaymentModalView,
  itemsBalanceModalView,
  buyItemsModalView,
  walletPayModalView,
  terminalModalView,
  personalAccountModalView,
  profilePromotionItemId,
  profilePromotionItemsModalView,
  profileEditSuccessMessage,
  packagePayModalView,
  successWebViewPaymentStatusModalView,
  failureWebViewPaymentStatusModalView,
  notification,
  replenishmentNotifications,
  replenishmentNotificationsModal,
  userCards,
  adriverBanner,
  currentItem,
  profile,
  modalLock
});
